import { useMemo, useState, Dispatch, SetStateAction } from 'react';
import { format } from 'date-fns';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import type { Payment, ListPaymentsInput } from '@erp/rpc';
import { SystemEntity } from '@erp/shared-types';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SystemEntityInline } from '@/components/system-entities';
import paths from '@/paths';
import { useApprovePayments } from '@/api/rpc/hooks/finance/useApprovePayments';

interface PaymentsTableProps {
  payments: Payment[];
  total: number;
  paginationState: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  filters: ListPaymentsInput;
}

function formatDate(value?: string | null | Date, withTime = false) {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return format(date, withTime ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy');
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'paid':
      return 'default';
    case 'in_review':
    case 'approved':
    case 'processing':
    case 'scheduled':
    case 'pending':
      return 'secondary';
    case 'failed':
    case 'canceled':
      return 'destructive';
    default:
      return 'outline';
  }
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function PaymentsTable({
  payments,
  total,
  paginationState,
  onPaginationChange,
  filters,
}: PaymentsTableProps) {
  const navigate = useNavigate();
  const approvePayments = useApprovePayments();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Get selected payment IDs for bulk actions
  const selectedPaymentIds = useMemo(() => {
    return Object.entries(rowSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([paymentId]) => paymentId);
  }, [rowSelection]);

  // Get selected payments that are in_review (can be approved)
  const approvablePaymentIds = useMemo(() => {
    return selectedPaymentIds.filter((id) => {
      const payment = payments.find((p) => p.id === id);
      return payment?.status === 'in_review';
    });
  }, [selectedPaymentIds, payments]);

  const handleBulkApprove = () => {
    if (approvablePaymentIds.length === 0) return;

    approvePayments.mutate(
      {
        type: 'paymentIds',
        paymentIds: approvablePaymentIds,
      },
      {
        onSuccess: () => {
          setRowSelection({});
        },
      },
    );
  };

  const columns: ColumnDef<Payment>[] = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40,
      },
      {
        accessorKey: 'paymentDate',
        header: 'Payment Date',
        cell: ({ getValue }) => (
          <span className="text-sm text-muted-foreground">
            {formatDate(getValue<string | null>())}
          </span>
        ),
        size: 140,
      },
      {
        id: 'invoice',
        header: 'Invoice(s)',
        cell: ({ row }) => {
          const invoices = row.original.invoices;
          const legacyInvoice = row.original.invoice;

          if (invoices && invoices.length > 0) {
            const first = invoices[0];
            if (invoices.length === 1) {
              return (
                <span className="text-sm font-mono">
                  {first?.invoiceNumber || '—'}
                </span>
              );
            }
            return (
              <span className="text-sm font-mono">
                {first?.invoiceNumber || '—'}{' '}
                <span className="text-muted-foreground">
                  +{invoices.length - 1}
                </span>
              </span>
            );
          }

          if (legacyInvoice) {
            return (
              <span className="text-sm font-mono">
                {legacyInvoice.invoiceNumber || '—'}
              </span>
            );
          }
          return <span className="text-sm text-muted-foreground">—</span>;
        },
      },
      {
        id: 'vendor',
        header: 'Vendor',
        cell: ({ row }) => {
          const invoices = row.original.invoices;
          const legacyInvoice = row.original.invoice;
          const supplierId =
            invoices?.[0]?.supplierId ?? legacyInvoice?.supplierId;

          if (supplierId) {
            return (
              <SystemEntityInline
                entityType={SystemEntity.AP_SUPPLIER}
                entityId={supplierId}
              />
            );
          }
          return <span className="text-sm text-muted-foreground">—</span>;
        },
      },
      {
        accessorKey: 'method',
        header: 'Method',
        cell: ({ getValue }) => (
          <Badge variant="outline" className="uppercase">
            {getValue<string>() || 'ACH'}
          </Badge>
        ),
        size: 100,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue<string>();
          return (
            <Badge variant={getStatusVariant(status)}>
              {formatStatus(status)}
            </Badge>
          );
        },
        size: 140,
      },
      {
        id: 'amount',
        header: 'Amount',
        cell: ({ row }) => {
          const amount = Number(row.original.amount ?? '0');
          const currency = row.original.currency || 'USD';
          const validAmount = Number.isFinite(amount) ? amount : 0;
          return (
            <span className="text-sm font-semibold">
              {validAmount.toLocaleString(undefined, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
              })}
            </span>
          );
        },
        size: 140,
      },
      {
        id: 'memo',
        header: 'Memo',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
            {row.original.memo || '—'}
          </span>
        ),
      },
    ],
    [],
  );

  const pageCount = Math.ceil(total / paginationState.pageSize);

  const table = useReactTable({
    data: payments,
    columns,
    manualPagination: true,
    pageCount: pageCount > 0 ? pageCount : -1,
    state: {
      pagination: paginationState,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onRowClick = (payment: Payment) => {
    navigate({
      to: paths.authenticated.accountsPayable.payment,
      params: { paymentId: payment.id },
    });
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {selectedPaymentIds.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
          <span className="text-sm text-muted-foreground">
            {selectedPaymentIds.length} payment
            {selectedPaymentIds.length !== 1 ? 's' : ''} selected
          </span>
          {approvablePaymentIds.length > 0 && (
            <Button
              size="sm"
              onClick={handleBulkApprove}
              disabled={approvePayments.isPending}
            >
              {approvePayments.isPending
                ? 'Approving...'
                : `Approve ${approvablePaymentIds.length} Payment${approvablePaymentIds.length !== 1 ? 's' : ''}`}
            </Button>
          )}
          {approvablePaymentIds.length < selectedPaymentIds.length && (
            <span className="text-xs text-muted-foreground">
              ({selectedPaymentIds.length - approvablePaymentIds.length} not in
              review)
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>
            Clear Selection
          </Button>
        </div>
      )}

      <DataTable table={table} onRowClick={onRowClick} />
      <DataTablePagination table={table} />
    </div>
  );
}
